/**
 * WCC360 v2 — Careers Bundle loader.
 *
 * Loads the dictionary-encoded corpus of 5,877 careers from
 * `/data/careers_bundle_v1.json`, decodes it once, and memoises the
 * fully-expanded rows. React never sees the encoded form.
 *
 * Also exposes the bundled market fallback (salary_lo/hi, demand_2040,
 * automation_exposure) for 2,756 rows, and a `resolveMarket` helper that
 * enforces the precedence rule:
 *
 *   live (Supabase)  →  seed (Supabase)  →  bundled fallback  →  neutral
 *

 * A career with **neither live nor fallback market data** MUST NOT be
 * shown as a Best match. `hasPricedMarket(row)` is the gate for that.
 * The engine also applies the same cap to `confidence === "low"` rows.
 */
 
export const BUNDLE_URL = "/data/careers_bundle_v1.json";

// ---- Raw encoded shape (matches the bundle spec) ------------------------

interface RawBundle {
  v: string | number;
  n: number;
  schema: string[];
  fields: string[]; // 20 field names, in row order
  archetypes: string[]; // 8, index into row[6]/row[8]
  dicts: Record<string, string[]>;
  families: string[];
  clusters: string[];
  levels: string[];
  confidence: string[];
  entry_requirements: string[];
  market: {
    fallback: {
      // [salary_lo_lpa, salary_hi_lpa, demand_2040 band, automation_exposure %]
      // NOTE: demand_2040 is a categorical band ("High" | "Medium" | "Low"),
      // NOT a 0..1 score. Do not coerce to number.
      by_row: Record<string, [number, number, DemandBand, number]>;
    };
  };
  rows: RawRow[];
}

// Positional: [ ci_id, name, famIdx, clusIdx, lvlIdx, fieldIdx[20],
//               arch1Idx, arch1Pct, arch2Idx, arch2Pct, entryIdx, confIdx ]
type RawRow = [
  string,        // 0 ci_id
  string,        // 1 name
  number,        // 2 family idx
  number,        // 3 cluster idx
  number,        // 4 level idx
  number[],      // 5 field idx array (len 20, -1 = null)
  number,        // 6 arch1 idx
  number,        // 7 arch1 pct
  number,        // 8 arch2 idx (-1 = null)
  number,        // 9 arch2 pct
  number,        // 10 entry_requirement idx
  number         // 11 confidence idx
];

// ---- Decoded shape used everywhere else in the app ----------------------

export type ArchetypeName =
  | "Builder" | "Thinker" | "Creator" | "Connector"
  | "Organizer" | "Leader" | "Caregiver" | "Advocate";

export interface CareerRow {
  rowIndex: number;          // position in bundle.rows, used to key market fallback
  ciId: string;
  name: string;
  family: string;
  cluster: string;
  level: string;
  entryRequirement: string | null;
  confidence: string;         // "high" | "medium" | "low" (etc.)
  /** Named field values, decoded via dicts. Missing / null fields dropped. */
  fields: Record<string, string>;
  archetypes: Array<{ name: string; pct: number }>; // 1 or 2 entries
}

export type DemandBand = "High" | "Medium" | "Low";

export interface MarketData {
  salaryLoLpa: number | null;
  salaryHiLpa: number | null;
  /** Categorical band from fallback; live source may replace with same shape. */
  demand2040: DemandBand | null;
  automationExposure: number | null; // percentage 0..100 from fallback
  source: "live" | "seed" | "fallback" | "neutral";
  /** Non-seed row older than 24h: used and flagged, never discarded. */
  stale?: boolean;
  /** How many postings the band came from. Low counts = low confidence. */
  observations?: number | null;
}

export interface CareersBundle {
  version: string;
  count: number;
  archetypes: string[];
  families: string[];
  clusters: string[];
  rows: CareerRow[];
  /** Row-index-keyed fallback market data. */
  fallbackMarket: Record<number, [number, number, DemandBand, number]>;
}

// ---- Loader (memoised) --------------------------------------------------

let bundlePromise: Promise<CareersBundle> | null = null;

export function loadCareersBundle(): Promise<CareersBundle> {
  if (!bundlePromise) bundlePromise = fetchAndDecode();
  return bundlePromise;
}

async function fetchAndDecode(): Promise<CareersBundle> {
  const res = await fetch(BUNDLE_URL, { cache: "force-cache" });
  if (!res.ok) {
    throw new Error(
      `Careers bundle not found at ${BUNDLE_URL} (HTTP ${res.status}). ` +
      `Drop careers_bundle_v1.json into public/data/ to enable ranking.`,
    );
  }
  const raw = (await res.json()) as RawBundle;
  return decode(raw);
}

function decode(raw: RawBundle): CareersBundle {
  const rows: CareerRow[] = raw.rows.map((r, i) => {
    const fieldIdx = r[5] || [];
    const fields: Record<string, string> = {};
    for (let f = 0; f < raw.fields.length; f++) {
      const idx = fieldIdx[f];
      if (idx === undefined || idx === -1) continue;
      const fieldName = raw.fields[f];
      const dict = raw.dicts[fieldName];
      if (!dict) continue;
      const val = dict[idx];
      if (val !== undefined) fields[fieldName] = val;
    }

    const archetypes: Array<{ name: string; pct: number }> = [];
    if (r[6] !== -1) archetypes.push({ name: raw.archetypes[r[6]], pct: r[7] });
    if (r[8] !== -1) archetypes.push({ name: raw.archetypes[r[8]], pct: r[9] });

    return {
      rowIndex: i,
      ciId: r[0],
      name: r[1],
      family: r[2] === -1 ? "" : raw.families[r[2]],
      cluster: r[3] === -1 ? "" : raw.clusters[r[3]],
      level: r[4] === -1 ? "" : raw.levels[r[4]],
      entryRequirement: r[10] === -1 ? null : raw.entry_requirements[r[10]],
      confidence: r[11] === -1 ? "unknown" : raw.confidence[r[11]],
      fields,
      archetypes,
    };
  });

  // Coerce the fallback map keys from string → number for cheap lookups.
  const fallbackMarket: Record<number, [number, number, DemandBand, number]> = {};
  for (const [k, v] of Object.entries(raw.market?.fallback?.by_row ?? {})) {
    fallbackMarket[Number(k)] = v;
  }

  return {
    version: String(raw.v),
    count: raw.n ?? rows.length,
    archetypes: raw.archetypes,
    families: raw.families,
    clusters: raw.clusters,
    rows,
    fallbackMarket,
  };
}

// ---- Market resolution --------------------------------------------------

/**
 * A market lookup against the Supabase `career_market_intelligence` table:
 * returns null when there is no record for this ci_id. The implementation is
 * a single bulk read per request, indexed in memory and cached 1h — this
 * shape lets the engine consume it row-by-row without caring how it was
 * fetched.
 */
export type LiveMarketLookup = (
  ciId: string,
) => MarketData | null;

/**
 * Precedence: live → seed → bundled fallback → neutral.
 * Neutral means every price/demand/automation field is null; the caller
 * should treat null as "unknown" (score 50), never zero.
 */
export function resolveMarket(
  row: CareerRow,
  bundle: CareersBundle,
  live?: LiveMarketLookup,
): MarketData {
  if (live) {
    const hit = live(row.ciId);
    if (hit) return hit;
  }
  const fb = bundle.fallbackMarket[row.rowIndex];
  if (fb) {
    return {
      salaryLoLpa: fb[0],
      salaryHiLpa: fb[1],
      demand2040: fb[2],
      automationExposure: fb[3],
      source: "fallback",
    };
  }
  return {
    salaryLoLpa: null,
    salaryHiLpa: null,
    demand2040: null,
    automationExposure: null,
    source: "neutral",
  };
}

/**
 * The Best-match gate.
 *
 * A career can be a Best match only if:
 *   1. It has a resolvable salary (live or fallback), AND
 *   2. Its scoring confidence is not "low".
 *
 * Everything else can still be surfaced under "Also consider."
 *
 * This is the rule that stops a ₹12 LPA burnt-out engineer being offered
 * Brick Kiln Worker.
 */
export function isBestMatchEligible(
  row: CareerRow,
  market: MarketData,
): boolean {
  if (market.source === "neutral") return false;
  if (market.salaryLoLpa == null && market.salaryHiLpa == null) return false;
  if (row.confidence?.toLowerCase() === "low") return false;
  return true;
}
